import { apiClient } from './client';

// Types
export interface TokenValidationRequest {
  refresh_token: string;
}

export interface TokenValidationResponse {
  valid: boolean;
  access_token?: string;
  expires_in?: number;
  customer_ids?: string[];
  customers?: Array<{ id: string; name: string }>; // Add customer info with names
  error?: string;
}

export interface CustomerDetails {
  customer_id: string;
  name: string;
  system_count: number;
  cluster_count: number;
  total_capacity_tb: number;
}

export interface NetAppConfig {
  id: string;
  name?: string;
  last_discovered_at?: string | null;
  discovery_error?: string | null;
  customer_id: string | null;
  customer_name?: string;
  system_count?: number;
  cluster_count?: number;
  total_capacity_tb?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  schedules?: NetAppSchedule[];
}

export interface NetAppSchedule {
  id: string;
  config_id: string;
  schedule_type: 'frequent' | 'daily' | 'weekly' | 'monthly';
  enabled: boolean;
  interval_minutes?: number;
  time_of_day?: string;
  day_of_week?: number;
  day_of_month?: number;
  cron_expression?: string;
  next_run?: string;
  last_run?: string;
  created_at: string;
  updated_at: string;
  tasks?: NetAppTask[];
}

export interface NetAppTask {
  id: string;
  schedule_id: string;
  task_name: string;
  enabled: boolean;
  timeout_seconds: number;
  retry_count: number;
  query_template: string;
  created_at: string;
  updated_at: string;
}

export interface TaskHistory {
  id: string;
  task_id: string;
  execution_time: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  duration_ms?: number;
  records_collected?: number;
  error_message?: string;
  retry_attempt: number;
}

export interface HealthIssue {
  issue_id: string;
  customer_id: string;
  timestamp: string;
  severity: 'critical' | 'warning' | 'info';
  category: string;
  issue: string;
  action?: string;
  affected?: string;
  system_id?: string;
  aggregate_id?: string;
  cluster_id?: string;
  resolved: boolean;
  resolved_date?: string;
  resolution_notes?: string;
}

export interface HealthMetric {
  metric_id: string;
  customer_id: string;
  timestamp: string;
  metric_type: string;
  metric_value: number;
  metric_status?: string;
  metric_unit?: string;
  system_id?: string;
  aggregate_id?: string;
  cluster_id?: string;
  details?: Record<string, any>;
}

export interface ConfigurationWizardData {
  refresh_token: string;
  customer_id: string;
  schedules: Array<{
    schedule_type: 'frequent' | 'daily' | 'weekly' | 'monthly';
    enabled: boolean;
    interval_minutes?: number;
    time_of_day?: string;
    day_of_week?: number;
    day_of_month?: number;
    tasks?: string[];
  }>;
}

function extractFilenameFromHeaders(headers: unknown): string | null {
  const h = headers as Record<string, string> | undefined;
  const raw = h?.['content-disposition'] || h?.['Content-Disposition'];
  if (!raw) return null;
  const match = /filename="?([^";]+)"?/.exec(raw);
  return match ? match[1] : null;
}

// API Client
class NetAppAPI {
  // Token validation
  async validateToken(refreshToken: string): Promise<TokenValidationResponse> {
    const response = await apiClient.post<TokenValidationResponse>(
      '/netapp/validate-token',
      { refresh_token: refreshToken }
    );
    return response.data;
  }

  // Configuration management
  async createConfig(data: {
    customer_id?: string;
    name?: string;
    refresh_token: string;
    schedules?: any[];
  }): Promise<NetAppConfig> {
    const response = await apiClient.post<NetAppConfig>('/netapp/configs', data);
    return response.data;
  }

  async listConfigs(
    skip = 0,
    limit = 100,
    activeOnly = true
  ): Promise<NetAppConfig[]> {
    const response = await apiClient.get<NetAppConfig[]>('/netapp/configs', {
      params: { skip, limit, active_only: activeOnly },
    });
    return response.data;
  }

  async getConfig(configId: string): Promise<NetAppConfig> {
    const response = await apiClient.get<NetAppConfig>(`/netapp/configs/${configId}`);
    return response.data;
  }

  async updateConfig(
    configId: string,
    data: { name?: string; refresh_token?: string; is_active?: boolean }
  ): Promise<NetAppConfig> {
    const response = await apiClient.patch<NetAppConfig>(
      `/netapp/configs/${configId}`,
      data
    );
    return response.data;
  }

  async refreshConnection(configId: string): Promise<void> {
    await apiClient.post(`/netapp/configs/${configId}/refresh`);
  }

  async getConnectionCustomers(configId: string): Promise<Array<import('@/contexts/DashboardCustomerContext').DashboardCustomer & { system_count: number }>> {
    return (await apiClient.get(`/netapp/configs/${configId}/customers`)).data;
  }

  async deleteConfig(configId: string, deleteAllData = false): Promise<void> {
    await apiClient.delete(`/netapp/configs/${configId}`, {
      data: { delete_all_data: deleteAllData }
    });
  }

  // Execute initial tasks for a configuration
  async executeInitialTasks(configId: string, selectedTasks?: string[]): Promise<{
    successful: string[];
    failed: Array<{ task: string; error: string }>;
    skipped: Array<{ task: string; reason: string }>;
    total_duration_ms: number;
  }> {
    const response = await apiClient.post(`/netapp/configs/${configId}/execute-initial`, {}, {
      params: selectedTasks ? { selected_tasks: selectedTasks } : undefined
    });
    return response.data;
  }

  // Customer details
  async getCustomerDetails(customerId: string, refreshToken?: string): Promise<CustomerDetails> {
    // If we have a refresh token, pass it as a header
    const config = refreshToken ? {
      headers: { 'X-NetApp-Token': refreshToken }
    } : {};

    const response = await apiClient.get<CustomerDetails>(
      `/netapp/customers/${customerId}`,
      config
    );
    return response.data;
  }

  // Schedule management
  async createSchedule(
    configId: string,
    scheduleData: any
  ): Promise<NetAppSchedule> {
    const response = await apiClient.post<NetAppSchedule>(
      '/netapp/schedules',
      scheduleData,
      { params: { config_id: configId } }
    );
    return response.data;
  }

  async toggleSchedule(scheduleId: string): Promise<NetAppSchedule> {
    const response = await apiClient.post<NetAppSchedule>(
      `/netapp/schedules/${scheduleId}/toggle`
    );
    return response.data;
  }

  async testSchedule(scheduleId: string): Promise<{
    success: boolean;
    task_name?: string;
    message?: string;
    error?: string;
  }> {
    const response = await apiClient.post(
      `/netapp/schedules/${scheduleId}/test`
    );
    return response.data;
  }

  // Task history
  async getTaskHistory(
    taskId: string,
    limit = 100,
    status?: string
  ): Promise<TaskHistory[]> {
    const response = await apiClient.get<TaskHistory[]>(
      `/netapp/tasks/${taskId}/history`,
      { params: { limit, status } }
    );
    return response.data;
  }

  // Health monitoring
  async getHealthIssues(
    customerId: string,
    severity?: string,
    resolved = false,
    limit = 100
  ): Promise<HealthIssue[]> {
    const response = await apiClient.get<HealthIssue[]>(
      '/netapp/health/issues',
      { params: { customer_id: customerId, severity, resolved, limit } }
    );
    return response.data;
  }

  async updateHealthIssue(
    issueId: string,
    data: { resolved?: boolean; resolution_notes?: string }
  ): Promise<HealthIssue> {
    const response = await apiClient.patch<HealthIssue>(
      `/netapp/health/issues/${issueId}`,
      data
    );
    return response.data;
  }

  async getHealthMetrics(
    customerId: string,
    metricType?: string,
    startDate?: string,
    endDate?: string,
    limit = 1000
  ): Promise<HealthMetric[]> {
    const response = await apiClient.get<HealthMetric[]>(
      '/netapp/health/metrics',
      {
        params: {
          customer_id: customerId,
          metric_type: metricType,
          start_date: startDate,
          end_date: endDate,
          limit,
        },
      }
    );
    return response.data;
  }

  // Report generation
  async generateIBReview(configId: string, customerName?: string, customerId?: string): Promise<void> {
    const response = await apiClient.post(
      customerId
        ? `/dashboard/customers/${encodeURIComponent(customerId)}/reports/ib-review`
        : `/netapp/configs/${configId}/reports/ib-review`,
      {},
      { responseType: 'blob' }
    );
    const blob = new Blob([response.data as BlobPart], {
      type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dispositionFilename = extractFilenameFromHeaders(response.headers);
    const safeName = (customerName || 'NetApp')
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const today = new Date()
      .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      .replace(/ /g, '');
    link.download = dispositionFilename || `${safeName}-IB_Review-${today}.pptx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Configuration wizard
  async completeWizard(wizardData: ConfigurationWizardData): Promise<NetAppConfig> {
    // Use the createConfig endpoint instead of non-existent wizard endpoint
    const response = await apiClient.post<NetAppConfig>(
      '/netapp/configs',
      wizardData
    );
    return response.data;
  }
}

export const netappAPI = new NetAppAPI();